import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Paper, 
  TextField, 
  Button, 
  Snackbar, 
  Alert,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import Image from '../components/Image';

// Import images from centralized image config
import { BANNERS } from '../assets/images/imageConfig';

// Images from the centralized config
const CONTACT_BANNER = BANNERS.CONTACT;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: undefined
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-]{10,15}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // In a real application, you would send this data to a server
      console.log('Form submitted:', formData);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Thank you for contacting us! We will get back to you shortly.',
        severity: 'success'
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Please correct the errors in the form.',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // More aggressive approach to remove the Join Our Team section
  useEffect(() => {
    // Add CSS that will immediately hide any Join Our Team sections
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      /* Hide any Join Our Team sections */
      div:has(h2:contains('Join Our Team')),
      div:has(h3:contains('Join Our Team')),
      [class*="join-team"],
      [class*="career-section"],
      div:has(> div > h2:contains('Join')):has(> div > h3:contains('Team')),
      section:has(h2:contains('Join Our Team')),
      div:has(button:contains('Contact Us About Careers')),
      div:has(> div[style*="background-color: rgb(14, 36, 76)"]),
      div:has(> div[style*="background-color: rgb(0, 22, 64)"]) {
        display: none !important;
      }
    `;
    document.head.appendChild(styleElement);

    const removeJoinTeamSection = () => {
      // Direct removal by content
      const teamSectionIdentifiers = ['Join Our Team', 'rewarding career', 'talented individuals', 'share our vision'];
      
      // Look through all divs with a dark blue background
      document.querySelectorAll('div').forEach(div => {
        const bg = window.getComputedStyle(div).backgroundColor;
        if (bg.includes('rgb(0, 22, 64)') || bg.includes('rgb(14, 36, 76)') || bg.includes('rgb(19, 41, 61)')) {
          // Check if any of the text content includes our identifiers
          let shouldRemove = false;
          for (let identifier of teamSectionIdentifiers) {
            if (div.textContent.includes(identifier)) {
              shouldRemove = true;
              break;
            }
          }
          
          if (shouldRemove) {
            div.remove();
          }
        }
      });
      
      // Look for the button that links to careers
      document.querySelectorAll('a, button').forEach(element => {
        if (element.textContent.includes('Career') || 
            element.textContent.includes('Join') || 
            element.textContent.includes('Team')) {
          
          // Find parent container
          let parent = element.parentElement;
          for (let i = 0; i < 6; i++) {
            if (!parent || parent.tagName === 'BODY') break;
            
            // If we find a section with specific styling
            const bg = window.getComputedStyle(parent).backgroundColor;
            if (bg.includes('rgb(0, 22, 64)') || bg.includes('rgb(14, 36, 76)') || bg.includes('rgb(19, 41, 61)')) {
              parent.remove();
              return;
            }
            
            parent = parent.parentElement;
          }
        }
      });
      
      // Remove specifically the banner shown in the image
      document.querySelectorAll('div').forEach(div => {
        if (div.textContent.includes('Join Our Team') && 
            div.textContent.includes('Looking for a rewarding career?') &&
            div.textContent.includes('Contact Us About Careers')) {
          div.remove();
        }
      });
    };
    
    // Run immediately and with delays
    removeJoinTeamSection();
    const timers = [100, 500, 1000, 2000].map(delay => 
      setTimeout(removeJoinTeamSection, delay)
    );
    
    // Set up mutation observer to watch for dynamically added content
    const observer = new MutationObserver(removeJoinTeamSection);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      observer.disconnect();
      timers.forEach(clearTimeout);
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <Box>
      {/* Hero Banner */}
      <Box 
        sx={{
          position: 'relative',
          height: { xs: '200px', md: '300px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 86, 179, 0.85)',
              zIndex: 1
            }
          }}
        >
          <Image 
            src={CONTACT_BANNER}
            alt="Contact Us"
            sx={{ width: '100%', height: '100%' }}
          />
        </Box>
        <Box sx={{ position: 'relative', textAlign: 'center', zIndex: 1 }}>
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '3rem' } 
            }}
          >
            Contact Us
          </Typography>
          <Typography variant="h6">
            We'd love to hear from you
          </Typography>
        </Box>
      </Box>

      {/* Contact Form and Info */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom 
            color="primary"
            sx={{ fontWeight: 700 }}
          >
            Get In Touch
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', mb: 2 }}>
            Have questions about our services? Want to request a quote? Or simply want to know more about SafeNext?
          </Typography>
          <Divider sx={{ width: 80, mx: 'auto', borderColor: 'primary.main', borderWidth: 2 }} />
        </Box>

        <Grid container spacing={5}>
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={3}
              component="form"
              onSubmit={handleSubmit}
              sx={{ 
                p: 4, 
                borderRadius: 4
              }}
            >
              <Typography variant="h5" gutterBottom color="primary" fontWeight={600}>
                Send us a message
              </Typography>
              <Typography variant="body2" paragraph color="text.secondary">
                Fill out the form below, and we'll get back to you as soon as possible.
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    error={Boolean(errors.phone)}
                    helperText={errors.phone}
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Your Message"
                    name="message"
                    multiline
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    error={Boolean(errors.message)}
                    helperText={errors.message}
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    endIcon={<SendIcon />}
                    sx={{ 
                      py: 1.5, 
                      px: 4,
                      borderRadius: 2
                    }}
                  >
                    Send Message
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom color="primary" fontWeight={600}>
                  Contact Information
                </Typography>
                <Typography variant="body2" paragraph color="text.secondary">
                  You can also reach out to us directly using the contact information below:
                </Typography>
                
                <List>
                  <ListItem sx={{ pb: 3 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <LocationOnIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary="Head Office"
                      secondary={
                        <>
                          <Typography variant="body2" component="span" display="block">
                            Pune, Maharashtra, India
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  
                  <Divider variant="inset" component="li" />
                  
                  <ListItem sx={{ py: 3 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <PhoneIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary="Phone Numbers"
                      secondary={
                        <>
                          <Typography variant="body2" component="span" display="block">
                            Contact: +91 7668888811
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  
                  <Divider variant="inset" component="li" />
                  
                  <ListItem sx={{ py: 3 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <EmailIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary="Email Addresses"
                      secondary={
                        <>
                          <Typography variant="body2" component="span" display="block">
                            Email: shikhar482006@gmail.com
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  
                  <Divider variant="inset" component="li" />
                  
                  <ListItem sx={{ pt: 3 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <AccessTimeIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary="Business Hours"
                      secondary={
                        <>
                          <Typography variant="body2" component="span" display="block">
                            Monday to Friday: 9:00 AM - 6:00 PM
                          </Typography>
                          <Typography variant="body2" component="span" display="block">
                            Saturday: 9:00 AM - 1:00 PM (Customer Support only)
                          </Typography>
                          <Typography variant="body2" component="span" display="block">
                            Sunday: Closed
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Success/Error Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact; 